import { useEffect, useRef } from "react";
import { DataKeys } from "../../types";
import { getLocalized, HealthLang } from "./healthTypes";
import { healthFocusVisible } from "./healthVisualStyles";

interface HealthLifecycleHorizontalSelectorProps {
    stages: DataKeys[];
    activeIndex: number;
    onSelect: (index: number) => void;
    lang: HealthLang;
}

/**
 * Reference horizontal stage bar — concept JSX lines 235–268 (lg:hidden).
 */
export function HealthLifecycleHorizontalSelector({
    stages,
    activeIndex,
    onSelect,
    lang,
}: HealthLifecycleHorizontalSelectorProps) {
    const chipRefs = useRef<(HTMLButtonElement | null)[]>([]);
    const containerRef = useRef<HTMLDivElement>(null);
    const progressPct = ((activeIndex + 1) / stages.length) * 100;

    useEffect(() => {
        const container = containerRef.current;
        const chip = chipRefs.current[activeIndex];
        if (!container || !chip) return;

        const containerRect = container.getBoundingClientRect();
        const chipRect = chip.getBoundingClientRect();
        const visible =
            chipRect.left >= containerRect.left &&
            chipRect.right <= containerRect.right;

        if (!visible) {
            chip.scrollIntoView({
                behavior: "smooth",
                inline: "center",
                block: "nearest",
            });
        }
    }, [activeIndex]);

    return (
        <div className="w-full min-w-0 text-left [text-align:left] lg:hidden">
            <div className="mb-2 grid w-full grid-cols-5 gap-1 text-center text-[10px] font-semibold text-grey-400 sm:text-[11px]">
                {stages.map((stage, index) => (
                    <span
                        key={stage.id ?? index}
                        className={`[text-align:center] ${
                            index === activeIndex ? "text-teal-500" : ""
                        }`}
                    >
                        {getLocalized(stage?.label, lang)}
                    </span>
                ))}
            </div>
            <div
                className="relative h-2 w-full rounded-full bg-teal-100"
                aria-hidden="true"
            >
                <div
                    className="absolute left-0 top-0 h-2 rounded-full bg-teal-500 transition-[width] duration-200 ease-out"
                    style={{ width: `${progressPct}%` }}
                />
            </div>
            <div
                ref={containerRef}
                className="mt-3 flex w-full max-w-full snap-x snap-mandatory gap-2 overflow-x-auto scroll-smooth pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
                role="tablist"
                aria-label="Care lifecycle stages"
            >
                {stages.map((stage, index) => {
                    const isActive = index === activeIndex;
                    const stepLabel = getLocalized(stage?.label, lang);
                    const stepTitle = getLocalized(stage?.title, lang);
                    return (
                        <button
                            key={stage.id ?? index}
                            ref={(el) => {
                                chipRefs.current[index] = el;
                            }}
                            type="button"
                            role="tab"
                            aria-selected={isActive}
                            onClick={() => onSelect(index)}
                            className={`max-w-[min(72vw,16rem)] snap-center shrink-0 rounded-full border px-3 py-1.5 text-left text-xs font-medium transition sm:max-w-none sm:px-4 sm:py-2 sm:text-sm ${healthFocusVisible} ${
                                isActive
                                    ? "border-teal-300 bg-teal-50 text-teal-500 shadow-sm"
                                    : "border-grey-200 bg-white text-grey-600"
                            }`}
                        >
                            <span className="[text-align:left]">
                                {stepLabel}{" "}
                                <span className="whitespace-nowrap sm:whitespace-normal">
                                    {stepTitle}
                                </span>
                            </span>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}
